import React, { useMemo, useRef, useState } from 'react';
import ImageCropModal from './ImageCropModal';
import MarkdownContent from './MarkdownContent';

const TABLE_TEMPLATE = `
| Column 1 | Column 2 | Column 3 |
| -------- | -------- | -------- |
| Cell 1   | Cell 2   | Cell 3   |
| Cell 4   | Cell 5   | Cell 6   |
`.trim();

export default function MarkdownEditor({ value, onChange, placeholder, minHeight = 200, onUploadImage, uploadDisabled }) {
  const textareaRef = useRef(null);
  const contentImageInputRef = useRef(null);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [viewMode, setViewMode] = useState('write');

  const stats = useMemo(() => {
    const text = value || '';
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    return { words, characters: text.length };
  }, [value]);

  function applyReplacement(start, end, replacement, selectionStart, selectionEnd) {
    const ta = textareaRef.current;
    const before = value.substring(0, start);
    const after = value.substring(end);
    onChange(before + replacement + after);
    setTimeout(() => {
      if (!ta) return;
      ta.focus();
      const nextStart = selectionStart ?? start + replacement.length;
      const nextEnd = selectionEnd ?? nextStart;
      ta.setSelectionRange(nextStart, nextEnd);
    }, 0);
  }

  function getSelectionInfo() {
    const ta = textareaRef.current;
    if (!ta) return null;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    return { start, end, selected: value.substring(start, end) };
  }

  function insertAtCursor(text) {
    const info = getSelectionInfo();
    if (!info) return;
    applyReplacement(info.start, info.end, text);
  }

  function handleInsertTable() {
    insertAtCursor(`\n${TABLE_TEMPLATE}\n`);
  }

  function wrapSelection(prefix, suffix = prefix, fallbackText = '') {
    const info = getSelectionInfo();
    if (!info) return;
    const baseText = info.selected || fallbackText;
    const replacement = `${prefix}${baseText}${suffix}`;
    const hasSelection = Boolean(info.selected);
    if (hasSelection) {
      applyReplacement(info.start, info.end, replacement, info.start + prefix.length, info.start + prefix.length + baseText.length);
    } else {
      applyReplacement(
        info.start,
        info.end,
        replacement,
        info.start + prefix.length,
        info.start + prefix.length + baseText.length
      );
    }
  }

  function transformSelectedLines(mapLine, fallbackLine) {
    const info = getSelectionInfo();
    if (!info) return;
    const source = info.selected || fallbackLine;
    const lines = source.split('\n');
    const replacement = lines.map(mapLine).join('\n');
    applyReplacement(info.start, info.end, replacement);
  }

  function handleBold() {
    wrapSelection('**', '**', 'Bold text');
  }

  function handleItalic() {
    wrapSelection('*', '*', 'Italic text');
  }

  function handleStrike() {
    wrapSelection('~~', '~~', 'Strikethrough');
  }

  function handleInlineCode() {
    wrapSelection('`', '`', 'inline-code');
  }

  function handleHeading(level) {
    transformSelectedLines((line) => {
      const clean = line.replace(/^#{1,6}\s+/, '');
      if (!clean.trim()) return '';
      return `${'#'.repeat(level)} ${clean}`;
    }, `Heading ${level}`);
  }

  function handleParagraph() {
    transformSelectedLines((line) => line.replace(/^#{1,6}\s+/, ''), 'Paragraph text');
  }

  function handleBlockquote() {
    transformSelectedLines((line) => (line.trim() ? `> ${line.replace(/^>\s?/, '')}` : line), 'Quoted text');
  }

  function handleChecklist() {
    transformSelectedLines((line) => (line.trim() ? `- [ ] ${line.replace(/^- \[.\]\s+/, '')}` : line), 'Task item');
  }

  function handleCodeBlock() {
    const info = getSelectionInfo();
    if (!info) return;
    const selected = info.selected || 'Code goes here';
    const replacement = `\n\`\`\`\n${selected}\n\`\`\`\n`;
    applyReplacement(info.start, info.end, replacement);
  }

  function handleLink() {
    const info = getSelectionInfo();
    if (!info) return;
    const selected = info.selected || 'Link text';
    const url = window.prompt('Enter URL', 'https://');
    if (!url) return;
    applyReplacement(info.start, info.end, `[${selected}](${url})`);
  }

  function handleParagraphBreak() {
    insertAtCursor('\n\n');
  }

  function handleSectionDivider() {
    insertAtCursor('\n\n---\n\n');
  }

  const BULLET_LIST_PREFIX = '- ';
  const NUMBERED_LIST_TEMPLATE = '1. \n2. \n3. ';

  function handleInsertBulletList() {
    transformSelectedLines(
      (line) => (line.trim() ? `${BULLET_LIST_PREFIX}${line.replace(/^[-*+]\s+/, '')}` : line),
      'List item'
    );
  }

  function handleInsertNumberedList() {
    const info = getSelectionInfo();
    if (!info) return;
    const selectedLines = (info.selected || NUMBERED_LIST_TEMPLATE).split('\n').filter((line) => line.trim());
    const replacement = selectedLines.map((line, i) => `${i + 1}. ${line.replace(/^\d+\.\s+/, '')}`).join('\n');
    applyReplacement(info.start, info.end, replacement);
  }

  function handleInsertImageClick() {
    if (uploadDisabled || !onUploadImage) return;
    contentImageInputRef.current?.click();
  }

  function handleContentImageChange(e) {
    const file = e.target.files?.[0];
    if (!file || !onUploadImage) return;
    setCropImageSrc(URL.createObjectURL(file));
    e.target.value = '';
  }

  async function handleContentCropComplete(blob) {
    if (!cropImageSrc || !onUploadImage) return;
    const urlToRevoke = cropImageSrc;
    try {
      const url = await onUploadImage(blob);
      insertAtCursor(`\n![image](${url})\n`);
    } catch (err) {
      console.error(err);
    } finally {
      URL.revokeObjectURL(urlToRevoke);
      setCropImageSrc(null);
    }
  }

  function handleContentCropCancel() {
    if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
    setCropImageSrc(null);
  }

  return (
    <div className="markdown-editor">
      <div className="markdown-toolbar">
        <div className="markdown-toolbar-row">
          <div className="markdown-toolbar-group">
            <button type="button" className="toolbar-btn" onClick={handleParagraph} title="Paragraph">
              P
            </button>
            <button type="button" className="toolbar-btn" onClick={() => handleHeading(1)} title="Heading 1">
              H1
            </button>
            <button type="button" className="toolbar-btn" onClick={() => handleHeading(2)} title="Heading 2">
              H2
            </button>
            <button type="button" className="toolbar-btn" onClick={() => handleHeading(3)} title="Heading 3">
              H3
            </button>
          </div>

          <div className="markdown-toolbar-group">
            <button type="button" className="toolbar-btn" onClick={handleBold} title="Bold">
              Bold
            </button>
            <button type="button" className="toolbar-btn" onClick={handleItalic} title="Italic">
              Italic
            </button>
            <button type="button" className="toolbar-btn" onClick={handleStrike} title="Strikethrough">
              Strike
            </button>
            <button type="button" className="toolbar-btn" onClick={handleInlineCode} title="Inline code">
              Code
            </button>
          </div>

          <div className="markdown-toolbar-group">
            <button type="button" className="toolbar-btn" onClick={handleInsertBulletList} title="Bullet list">
              Bullets
            </button>
            <button type="button" className="toolbar-btn" onClick={handleInsertNumberedList} title="Numbered list">
              Numbers
            </button>
            <button type="button" className="toolbar-btn" onClick={handleChecklist} title="Checklist">
              Checklist
            </button>
            <button type="button" className="toolbar-btn" onClick={handleBlockquote} title="Quote">
              Quote
            </button>
          </div>

          <div className="markdown-toolbar-group">
            <button type="button" className="toolbar-btn" onClick={handleLink} title="Insert link">
              Link
            </button>
            <button type="button" className="toolbar-btn" onClick={handleCodeBlock} title="Code block">
              Code block
            </button>
            <button type="button" className="toolbar-btn" onClick={handleInsertTable} title="Insert table">
              Table
            </button>
            <button type="button" className="toolbar-btn" onClick={handleSectionDivider} title="Insert divider">
              Divider
            </button>
            <button type="button" className="toolbar-btn" onClick={handleParagraphBreak} title="Paragraph break">
              Break
            </button>
          </div>
        </div>

        <div className="markdown-toolbar-row markdown-toolbar-row-secondary">
          <div className="markdown-toolbar-group">
            <button
              type="button"
              className={`view-btn ${viewMode === 'write' ? 'active' : ''}`}
              onClick={() => setViewMode('write')}
              title="Write mode"
            >
              Write
            </button>
            <button
              type="button"
              className={`view-btn ${viewMode === 'split' ? 'active' : ''}`}
              onClick={() => setViewMode('split')}
              title="Split mode"
            >
              Split
            </button>
            <button
              type="button"
              className={`view-btn ${viewMode === 'preview' ? 'active' : ''}`}
              onClick={() => setViewMode('preview')}
              title="Preview mode"
            >
              Preview
            </button>
          </div>

          <span className="toolbar-hint">Tip: select text first to apply formatting to it directly.</span>
        </div>

        {onUploadImage && (
          <div className="markdown-toolbar-row">
            <div className="markdown-toolbar-group">
            <button type="button" className="toolbar-btn" onClick={handleInsertImageClick} disabled={uploadDisabled} title="Upload and insert image">
              Upload image
            </button>
            </div>
            <input
              ref={contentImageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleContentImageChange}
              style={{ display: 'none' }}
            />
          </div>
        )}
      </div>
      {cropImageSrc && (
        <ImageCropModal
          imageSrc={cropImageSrc}
          aspect={undefined}
          onComplete={handleContentCropComplete}
          onCancel={handleContentCropCancel}
        />
      )}

      <div className={`markdown-workspace mode-${viewMode}`}>
        {viewMode !== 'preview' && (
          <div className="markdown-pane markdown-editor-pane">
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              className="markdown-textarea"
              style={{ minHeight: `${minHeight}px` }}
            />
          </div>
        )}

        {viewMode !== 'write' && (
          <div className="markdown-pane markdown-preview-pane" style={{ minHeight: `${minHeight}px` }}>
            <MarkdownContent content={value || '*Nothing to preview yet.*'} />
          </div>
        )}
      </div>

      <div className="markdown-statusbar">
        <span>{stats.words} words</span>
        <span>{stats.characters} characters</span>
      </div>
    </div>
  );
}
